import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  LinearProgress,
  Tooltip,
  CircularProgress,
  Divider,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import trainingModules from "../constants/trainingModules";
import api from "../api/client";

const PROVIDERS = [
  { label: "Highway Rangers", value: "highway" },
  { label: "Responder Safety", value: "responder" },
  { label: "FHWA", value: "fhwa" },
  { label: "FEMA", value: "fema" },
  { label: "Other", value: "other" },
];

const RESPONDER_TRACKS = [
  { label: "Regular Ranger", value: "regular" },
  { label: "Leader", value: "leader" },
  { label: "Disaster Response", value: "disaster" },
];

function Training() {
  const [provider, setProvider] = useState(PROVIDERS[0].value);
  const [uploadDialog, setUploadDialog] = useState({
    open: false,
    video: null,
    file: null,
    date: "",
    acknowledged: false,
    error: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [applyingNational, setApplyingNational] = useState(false);
  const [requirementsDialog, setRequirementsDialog] = useState({ open: false, course: null });

  // Fetch certifications from the server
  const fetchCertifications = useCallback(async () => {
    try {
      const res = await api.get("/training-certifications/my");
      setCertifications(res.data || []);
    } catch (err) {
      console.error("Failed to load certifications:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCertifications();
  }, [fetchCertifications]);

  // Build a map: courseId → certification object (most recent)
  const certByCourse = useMemo(() => {
    const map = {};
    for (const cert of certifications) {
      // If multiple certs for same course, prefer approved > pending > rejected
      const existing = map[cert.courseId];
      if (!existing
        || (cert.status === "approved" && existing.status !== "approved")
        || (cert.status === "pending" && existing.status === "rejected")) {
        map[cert.courseId] = cert;
      }
    }
    return map;
  }, [certifications]);

  // Only approved certs count towards completion
  const completedCourseIds = useMemo(
    () => new Set(certifications.filter((c) => c.status === "approved").map((c) => c.courseId)),
    [certifications]
  );

  const filteredVideos = useMemo(
    () => trainingModules.filter((video) => video.provider === provider),
    [provider]
  );
  const nationalHighwayRequiredCourseIds = useMemo(
    () => trainingModules
      .filter((module) => module.provider === "responder" || module.provider === "fema")
      .map((module) => module.id),
    []
  );
  const nationalHighwayMissingCourseIds = useMemo(
    () => nationalHighwayRequiredCourseIds.filter((courseId) => !completedCourseIds.has(courseId)),
    [nationalHighwayRequiredCourseIds, completedCourseIds]
  );
  const nationalHighwayEligible = nationalHighwayMissingCourseIds.length === 0;
  const totalCourses = trainingModules.length;
  const completedCourseCount = completedCourseIds.size;
  const progressValue = totalCourses === 0 ? 0 : Math.round((completedCourseCount / totalCourses) * 100);
  const pendingCount = useMemo(
    () => certifications.filter((c) => c.status === "pending").length,
    [certifications]
  );
  const providerProgress = useMemo(() => {
    const statsMap = {};

    trainingModules.forEach((module) => {
      if (!statsMap[module.provider]) {
        statsMap[module.provider] = { total: 0, completed: 0 };
      }
      statsMap[module.provider].total += 1;
      if (completedCourseIds.has(module.id)) {
        statsMap[module.provider].completed += 1;
      }
    });

    const orderedProviderKeys = [
      ...PROVIDERS.map((option) => option.value),
      ...Object.keys(statsMap).filter(
        (providerKey) => !PROVIDERS.some((providerOption) => providerOption.value === providerKey)
      ),
    ];

    return orderedProviderKeys
      .map((providerKey) => {
        const stats = statsMap[providerKey];
        if (!stats) {
          return null;
        }
        const label =
          PROVIDERS.find((option) => option.value === providerKey)?.label ||
          providerKey.replace(/^[a-z]/, (char) => char.toUpperCase());
        const percent = stats.total === 0 ? 0 : Math.round((stats.completed / stats.total) * 100);
        return { provider: providerKey, label, total: stats.total, completed: stats.completed, percent };
      })
      .filter(Boolean);
  }, [completedCourseIds]);

  const handleOpenUpload = (video) => {
    setUploadDialog({ open: true, video, file: null, date: "", acknowledged: false, error: "" });
  };

  const handleCloseUpload = () => {
    setUploadDialog((prev) => ({ ...prev, open: false }));
  };

  const handleOpenRequirements = (course) => {
    setRequirementsDialog({ open: true, course });
  };

  const handleCloseRequirements = () => {
    setRequirementsDialog({ open: false, course: null });
  };

  const handleUploadSubmit = async () => {
    if (!uploadDialog.file) {
      setUploadDialog((prev) => ({ ...prev, error: "Please upload your PDF certification before submitting." }));
      return;
    }

    const isPdf = uploadDialog.file.name?.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setUploadDialog((prev) => ({ ...prev, error: "Only PDF certification files are accepted." }));
      return;
    }

    if (!uploadDialog.date) {
      setUploadDialog((prev) => ({ ...prev, error: "Select the date you completed this training." }));
      return;
    }

    if (!uploadDialog.acknowledged) {
      setUploadDialog((prev) => ({ ...prev, error: "You must acknowledge the certification statement." }));
      return;
    }

    const video = uploadDialog.video;
    if (!video) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadDialog.file);
      formData.append("courseId", video.id);
      formData.append("courseName", video.title);
      formData.append("provider", video.provider);
      formData.append("completionDate", uploadDialog.date);

      await api.post("/training-certifications/upload", formData);

      setUploadDialog({ open: false, video: null, file: null, date: "", acknowledged: false, error: "" });
      setSnackbar({ open: true, message: "Certification uploaded — pending supervisor review.", severity: "success" });
      fetchCertifications();
    } catch (err) {
      const msg = err.response?.data || err.message || "Upload failed";
      setUploadDialog((prev) => ({ ...prev, error: typeof msg === "string" ? msg : "Upload failed. Please try again." }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCert = async (certId) => {
    try {
      await api.delete(`/training-certifications/${certId}`);
      setSnackbar({ open: true, message: "Certification removed.", severity: "success" });
      fetchCertifications();
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to delete certification.", severity: "error" });
    }
  };

  const handleApplyNationalHighway = async () => {
    setApplyingNational(true);
    try {
      const res = await api.post("/training-certifications/national-highway-ranger/apply");
      const certId = res?.data?.id;

      setSnackbar({
        open: true,
        message: "National Highway Ranger certificate granted and uploaded.",
        severity: "success",
      });

      await fetchCertifications();

      if (certId) {
        try {
          const downloadRes = await api.get(`/training-certifications/${certId}/download`);
          const downloadUrl = downloadRes?.data?.url;
          if (downloadUrl) {
            window.open(downloadUrl, "_blank", "noopener,noreferrer");
          }
        } catch (downloadErr) {
          console.error("Failed to open generated certificate download:", downloadErr);
        }
      }
    } catch (err) {
      const responseData = err?.response?.data;
      const message =
        (typeof responseData === "string" && responseData) ||
        responseData?.message ||
        err?.message ||
        "Could not apply for National Highway Ranger certificate.";

      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setApplyingNational(false);
    }
  };

  const renderModuleCard = (video) => (
    <Card elevation={3} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardMedia
        component="img"
        image={`${process.env.PUBLIC_URL}/${video.image || "rangeracademy.png"}`}
        alt={video.title}
        sx={{ height: 160, objectFit: "cover" }}
      />

      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {video.level && <Chip label={video.level} size="small" color="primary" />}
          {video.duration && <Chip label={video.duration} size="small" variant="outlined" />}
          {typeof video.creditHours === "number" && (
            <Chip
              label={`${video.creditHours} Credit Hour${video.creditHours === 1 ? "" : "s"}`}
              size="small"
              variant="outlined"
            />
          )}
          {video.focus && <Chip label={video.focus} size="small" variant="outlined" />}
        </Stack>
        <Typography variant="h6" component="h3" sx={{ mt: 1 }}>
          {video.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {video.summary}
        </Typography>
      </CardContent>

      <CardActions sx={{ px: 3, pb: 3 }}>
        {video.requirements && video.id !== "national-highway-ranger" ? (
          <Button variant="contained" fullWidth onClick={() => handleOpenRequirements(video)}>
            Requirements
          </Button>
        ) : video.prerequisiteIds && !video.prerequisiteIds.every((id) => completedCourseIds.has(id)) ? (
          <Stack spacing={1} width="100%" alignItems="center">
            <Tooltip
              title={`Complete all ${video.prerequisiteIds.length} prerequisite courses to unlock this module`}
              arrow
            >
              <Chip
                icon={<LockIcon />}
                label={`Locked — ${video.prerequisiteIds.filter((id) => completedCourseIds.has(id)).length}/${video.prerequisiteIds.length} prerequisites`}
                color="default"
                sx={{ fontWeight: 600, opacity: 0.8 }}
              />
            </Tooltip>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Complete all prerequisite courses first
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1} width="100%">
            {video.id !== "national-highway-ranger" && (
              <Button
                variant="contained"
                fullWidth
                component={video.url ? "a" : "button"}
                href={video.url || undefined}
                target={video.url ? "_blank" : undefined}
                rel={video.url ? "noopener noreferrer" : undefined}
                disabled={!video.url}
              >
                Start Module
              </Button>
            )}
            {(() => {
              const cert = certByCourse[video.id];
              if (!cert) {
                if (video.id === "national-highway-ranger") {
                  const completedCount = nationalHighwayRequiredCourseIds.length - nationalHighwayMissingCourseIds.length;
                  return (
                    <Stack spacing={1}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleApplyNationalHighway}
                        disabled={!nationalHighwayEligible || applyingNational}
                      >
                        {applyingNational ? <CircularProgress size={22} /> : "Apply for Certificate"}
                      </Button>
                      <Button variant="outlined" fullWidth onClick={() => handleOpenRequirements(video)}>
                        Requirements
                      </Button>
                      {!nationalHighwayEligible && (
                        <Typography variant="caption" color="text.secondary" textAlign="center">
                          Complete all RESPONDER SAFETY and FEMA modules ({completedCount}/{nationalHighwayRequiredCourseIds.length})
                        </Typography>
                      )}
                    </Stack>
                  );
                }

                return (
                  <Button variant="outlined" fullWidth onClick={() => handleOpenUpload(video)}>
                    Upload Certification
                  </Button>
                );
              }
              if (cert.status === "approved") {
                return (
                  <Stack spacing={1}>
                    <Chip label="Certified ✓" color="success" sx={{ fontWeight: 600 }} />
                    {video.id === "national-highway-ranger" && (
                      <Button variant="outlined" fullWidth onClick={() => handleOpenRequirements(video)}>
                        Requirements
                      </Button>
                    )}
                  </Stack>
                );
              }
              if (cert.status === "pending") {
                return (
                  <Stack spacing={1}>
                    <Chip label="Pending Review" color="warning" sx={{ fontWeight: 600 }} />
                    {video.id === "national-highway-ranger" && (
                      <Button variant="outlined" fullWidth onClick={() => handleOpenRequirements(video)}>
                        Requirements
                      </Button>
                    )}
                  </Stack>
                );
              }
              // rejected
              return (
                <Stack spacing={0.5}>
                  <Tooltip title={cert.reviewNotes || "No reviewer notes"} arrow>
                    <Chip label="Rejected" color="error" sx={{ fontWeight: 600, cursor: "help" }} />
                  </Tooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => handleDeleteCert(cert.id)}
                  >
                    Remove
                  </Button>
                  {video.id === "national-highway-ranger" ? (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleApplyNationalHighway}
                        disabled={!nationalHighwayEligible || applyingNational}
                      >
                        {applyingNational ? <CircularProgress size={18} /> : "Apply Again"}
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => handleOpenRequirements(video)}>
                        Requirements
                      </Button>
                    </>
                  ) : (
                    <Button size="small" variant="outlined" onClick={() => handleOpenUpload(video)}>
                      Re-upload
                    </Button>
                  )}
                </Stack>
              );
            })()}
          </Stack>
        )}
      </CardActions>
    </Card>
  );

  return (
    <DashboardLayout title="RangerAcademy">
      {pendingCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have {pendingCount} certification{pendingCount !== 1 ? "s" : ""} pending supervisor review.
        </Alert>
      )}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 3, md: 4 },
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
          alignItems: "stretch",
        }}
      >
        <Stack spacing={3}>
          <Stack spacing={2} direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }}>
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/rangeracademy.png`}
              alt="RangerAcademy logo"
              sx={{ width: 220, maxWidth: "100%", objectFit: "contain" }}
            />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                Welcome to the RangerAcademy
              </Typography>
              <Typography variant="body1" sx={{ mt: 1.5, maxWidth: 600 }}>
                Access training materials, certifications, and continuing education resources curated for Texas Highway
                Rangers. RangerAcademy keeps your skills sharp with always-on video modules, policy refreshers, and
                assessments tailored to your division.
              </Typography>
            </Box>
          </Stack>

          {providerProgress.length > 0 && (
            <Stack spacing={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Provider progress
              </Typography>
              <Grid container spacing={2}>
                {providerProgress.map((stat) => (
                  <Grid item xs={12} sm={6} key={stat.provider}>
                    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {stat.label}
                          </Typography>
                          <Chip label={`${stat.completed}/${stat.total}`} size="small" />
                        </Stack>
                        <LinearProgress variant="determinate" value={stat.percent} sx={{ height: 8, borderRadius: 4 }} />
                        <Typography variant="caption" color="text.secondary">
                          {stat.percent}% complete
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          )}
        </Stack>

        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 3,
            background: (theme) =>
              theme.palette.mode === "light"
                ? "linear-gradient(135deg, rgba(30,60,114,0.08), rgba(30,60,114,0.02))"
                : "linear-gradient(135deg, rgba(4,8,16,0.9), rgba(4,8,16,0.6))",
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Mastery Certifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete all RangerAcademy core modules plus the advanced curriculum below to earn the Mastery ribbon and access
              leadership-level assignments.
            </Typography>
            <Stack spacing={1.5}>
              {[
                { label: "Core Curriculum", description: "Finish every Core and Required module" },
                { label: "Advanced Roadway Ops", description: "Complete TIM ICS, Vehicle Fires, and MUTCD" },
                { label: "Special Initiatives", description: "Upload certifications for Move It or Work It and Special Hazards" },
              ].map((item) => (
                <Stack key={item.label} spacing={0.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mastery Progress
                </Typography>
                <Chip label={`${progressValue}%`} size="small" />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progressValue}
                sx={{ height: 12, borderRadius: 6, backgroundColor: (theme) => theme.palette.action.disabledBackground }}
              />
            </Box>
          </Stack>
        </Paper>
      </Paper>

      <Box sx={{ mt: 5 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Featured Training Modules
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Explore popular video modules and keep your qualifications current. Pick any session to stream instantly.
        </Typography>

        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Tabs
            value={provider}
            onChange={(_, value) => setProvider(value)}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
          >
            {PROVIDERS.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </Tabs>
        </Paper>

        {filteredVideos.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No training modules are available for this provider yet. Check back soon.
            </Typography>
          </Paper>
        ) : provider === "responder" ? (
          <Stack spacing={4}>
            {RESPONDER_TRACKS.map((trackDef) => {
              const trackModules = filteredVideos.filter((v) => v.track === trackDef.value);
              return (
                <Box key={trackDef.value}>
                  <Divider sx={{ mb: 2 }}>
                    <Chip label={trackDef.label} sx={{ fontWeight: 700, fontSize: "0.95rem", px: 2 }} />
                  </Divider>
                  {trackModules.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Modules coming soon.
                      </Typography>
                    </Paper>
                  ) : (
                    <Grid container spacing={3}>
                      {trackModules.map((video) => (
                        <Grid item xs={12} sm={6} lg={4} key={video.id}>
                          {renderModuleCard(video)}
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Grid container spacing={3}>
            {filteredVideos.map((video) => (
              <Grid item xs={12} sm={6} lg={4} key={video.id}>
                {renderModuleCard(video)}
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Dialog open={uploadDialog.open} onClose={handleCloseUpload} fullWidth maxWidth="sm">
        <DialogTitle>Upload Certification</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
          <Typography variant="body1">
            {uploadDialog.video ? `Course: ${uploadDialog.video.title}` : "Select a course to upload certification."}
          </Typography>
          <Stack spacing={2}>
            <Button variant="outlined" component="label">
              Choose PDF Certificate
              <input
                type="file"
                hidden
                accept="application/pdf"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  setUploadDialog((prev) => ({ ...prev, file, error: "" }));
                }}
              />
            </Button>
            {uploadDialog.file && (
              <Typography variant="body2" color="text.secondary">
                Selected file: {uploadDialog.file.name}
              </Typography>
            )}
            <TextField
              type="date"
              label="Completion Date"
              InputLabelProps={{ shrink: true }}
              value={uploadDialog.date}
              onChange={(event) => setUploadDialog((prev) => ({ ...prev, date: event.target.value, error: "" }))}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={uploadDialog.acknowledged}
                  onChange={(event) =>
                    setUploadDialog((prev) => ({ ...prev, acknowledged: event.target.checked, error: "" }))
                  }
                />
              }
              label="I certify that I have personally completed the full training course associated with the certification I am uploading. I understand that submitting false or misleading information may result in removal from the Texas Highway Rangers program."
            />
            {uploadDialog.error && <Alert severity="error">{uploadDialog.error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpload} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleUploadSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={22} /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: "", severity: "success" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ open: false, message: "", severity: "success" })} severity={snackbar.severity || "success"} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={requirementsDialog.open}
        onClose={handleCloseRequirements}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {requirementsDialog.course ? `${requirementsDialog.course.title} Requirements` : "Certification Requirements"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }} dividers>
          {requirementsDialog.course?.requirements?.intro && (
            <Typography variant="body1">
              {requirementsDialog.course.requirements.intro}
            </Typography>
          )}
          {requirementsDialog.course?.requirements?.sections?.map((section) => (
            <Stack key={section.title} spacing={0.75}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {section.title}
              </Typography>
              {section.body && (
                <Typography variant="body2" color="text.secondary">
                  {section.body}
                </Typography>
              )}
              {Array.isArray(section.bullets) && section.bullets.length > 0 && (
                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                  {section.bullets.map((bullet) => (
                    <Typography component="li" variant="body2" key={bullet} sx={{ color: "text.secondary" }}>
                      {bullet}
                    </Typography>
                  ))}
                </Box>
              )}
            </Stack>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRequirements}>Close</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Training;
